import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ArrowRight, Loader2, Heart, XCircle } from "lucide-react";

const TERMINAL = new Set(["paid", "failed", "expired"]);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [polling, setPolling] = useState(true);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setPolling(false); return; }
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setStatus(data);
        attempts.current += 1;
        if (TERMINAL.has(data.payment_status) || attempts.current > 20) {
          setPolling(false);
          return;
        }
        setTimeout(tick, 2000);
      } catch (err) {
        if (cancelled) return;
        // 404 -> session unknown, stop polling immediately.
        if (err?.response?.status === 404) {
          setStatus({ payment_status: "failed" });
          setPolling(false);
          return;
        }
        attempts.current += 1;
        if (attempts.current > 20) { setPolling(false); return; }
        setTimeout(tick, 2500);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  const paid = status.payment_status === "paid";
  const failed = ["failed", "expired"].includes(status.payment_status);
  const isCoaching = status.item_type === "coaching";
  const isCourse = status.item_type === "course";
  const nextHref = isCourse ? `/courses/${status.item_id}` : "/dashboard";

  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-24">
        <div className="rounded-[2rem] border border-neutral-100 card-soft p-10 lg:p-14 text-center">
          {polling && (
            <>
              <div className="w-16 h-16 mx-auto rounded-full bg-brand-blush flex items-center justify-center mb-6">
                <Loader2 className="w-7 h-7 text-brand-pink animate-spin" />
              </div>
              <div className="font-display text-3xl mb-2">Confirming your payment…</div>
              <div className="text-neutral-500">Hang tight, this usually takes a few seconds.</div>
            </>
          )}

          {!polling && paid && (
            <div data-testid="payment-success-panel">
              <div className="w-16 h-16 mx-auto rounded-full bg-brand-pink/15 flex items-center justify-center mb-6">
                <CheckCircle2 className="w-8 h-8 text-brand-pink" />
              </div>
              <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-3">Payment received</div>
              <h1 className="font-display text-4xl md:text-5xl mb-4">
                {isCoaching ? "Your session is reserved." : "Welcome inside."}
              </h1>
              <p className="text-neutral-600 leading-relaxed max-w-xl mx-auto mb-4">
                {isCoaching ? (
                  <>Your <b>{status.item_name}</b> is confirmed. Kristy (or her team) will reach out within one business day to lock in your time. Watch your inbox at the email you used to check out.</>
                ) : (
                  <>Your <b>{status.item_name}</b> is unlocked and attached to your account.
                    {isCourse && " Kristy just enrolled you — start Module 1 whenever you're ready."}</>
                )}
              </p>
              <div className="text-sm text-neutral-500 mb-8">
                Amount: <span className="font-medium text-neutral-900">${status.amount}</span> · Order id ends in <span className="font-mono">{sessionId?.slice(-6)}</span>
              </div>
              <div className="flex flex-wrap gap-3 justify-center">
                <Button asChild size="lg" className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white px-8" data-testid="payment-success-primary">
                  <Link to={nextHref}>
                    {isCourse ? "Start the course" : isCoaching ? "Explore free tools" : "Go to my library"} <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full border-neutral-300 px-8" data-testid="payment-success-dashboard">
                  <Link to={isCoaching ? "/toolbox" : "/dashboard"}>{isCoaching ? "Open Toolbox" : "Open dashboard"}</Link>
                </Button>
              </div>
              <div className="mt-10 text-xs uppercase tracking-widest text-neutral-400 inline-flex items-center gap-2">
                <Heart className="w-3 h-3 text-brand-pink" fill="#E91E63" /> Host Hustle™
              </div>
            </div>
          )}

          {!polling && failed && (
            <div data-testid="payment-failed-panel">
              <div className="w-16 h-16 mx-auto rounded-full bg-neutral-100 flex items-center justify-center mb-6">
                <XCircle className="w-8 h-8 text-neutral-500" />
              </div>
              <h1 className="font-display text-3xl mb-3">Payment didn't go through.</h1>
              <p className="text-neutral-600 mb-8">You weren't charged. Try again or reach out and Kristy will help.</p>
              <Button asChild className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white px-8">
                <Link to="/products">Back to storefront</Link>
              </Button>
            </div>
          )}

          {!polling && !paid && !failed && (
            <div>
              <div className="font-display text-2xl mb-3">Still processing.</div>
              <p className="text-neutral-600 mb-6">
                Your payment is taking a little longer than usual. We'll email you a receipt as soon as it settles — meanwhile you can head to your dashboard.
              </p>
              <Button asChild className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white px-8">
                <Link to="/dashboard">Open dashboard</Link>
              </Button>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

export function PaymentCancel() {
  const nav = useNavigate();
  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <section className="max-w-2xl mx-auto px-6 lg:px-10 py-24 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-neutral-100 flex items-center justify-center mb-6">
          <XCircle className="w-8 h-8 text-neutral-500" />
        </div>
        <h1 className="font-display text-4xl mb-3">Checkout cancelled.</h1>
        <p className="text-neutral-600 mb-8">No worries — nothing was charged. Come back whenever you're ready.</p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Button onClick={() => nav(-1)} data-testid="payment-cancel-back" className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white px-8">
            Try again
          </Button>
          <Button asChild variant="outline" className="rounded-full border-neutral-300 px-8">
            <Link to="/products">Back to storefront</Link>
          </Button>
        </div>
      </section>
      <Footer />
    </div>
  );
}
